import * as React from "react";
import dayjs from "dayjs";
import { DatePicker, Divider, theme, Segmented, Space, Button } from "antd";
import { DateRangeIcon } from "src/components/DateRangeIcon";
import { formatTime, RELATIVE_PRESETS } from "src/utils/date";
import { Flex } from "src/components/Flex";
import { usePopupContext } from "src/hooks/usePopup";
import { DateRangeInputClause, UseQuestionInputClauses } from "./definitions";
import { Clause } from "./Clause";
import { ClauseMenu, useMenuEscape } from "./ClauseMenu";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  HistoryOutlined,
} from "@ant-design/icons";
import styles from "./DateRangeClause.module.css";

export function DateRangeClause({
  clause,
  removeClause,
  updateClause,
}: {
  clause: DateRangeInputClause;
} & Pick<UseQuestionInputClauses, "removeClause" | "updateClause">) {
  return (
    <Clause
      onRemove={() => removeClause(clause)}
      onMenuClose={() => {
        if (
          !clause.value?.duration &&
          !clause.value?.startDate &&
          !clause.value?.endDate
        )
          removeClause(clause);
      }}
      renderMenu={() => (
        <DateRangeMenu updateClause={updateClause} clause={clause} />
      )}
      clause={clause}
      title="Date Range"
      icon={<DateRangeIcon size={10} style={{ opacity: 0.7 }} />}
    >
      <Flex inline start gap={3}>
        {clause.value ? (
          clause.value.duration ? (
            <span>{clause.value.duration}</span>
          ) : clause.value.startDate && clause.value.endDate ? (
            <>
              <span>{formatTime(clause.value.startDate, "YYYY/MM/DD")}</span>
              <span>-</span>
              <span>{formatTime(clause.value.endDate, "YYYY/MM/DD")}</span>
            </>
          ) : (
            <span></span>
          )
        ) : (
          <span></span>
        )}
      </Flex>
    </Clause>
  );
}

function DateRangeMenu({
  clause,
  updateClause,
}: {
  clause: DateRangeInputClause;
  updateClause: UseQuestionInputClauses["updateClause"];
}) {
  const [tempDateRange, setTempDateRange] = React.useState<{
    startDate?: string;
    endDate?: string;
  }>({
    startDate: clause.value?.startDate,
    endDate: clause.value?.endDate,
  });
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);

  const ref = React.useRef<any>(null);
  const popup = usePopupContext();
  const { token } = theme.useToken();

  useMenuEscape();

  // Group presets by category
  const presetGroups = React.useMemo(
    () => ({
      recent: RELATIVE_PRESETS.filter((p) =>
        ["P1D", "P2D", "P3D"].includes(p.value)
      ),
      weeks: RELATIVE_PRESETS.filter((p) =>
        ["T1W", "P7D", "L1W", "P14D"].includes(p.value)
      ),
      months: RELATIVE_PRESETS.filter((p) =>
        ["T1M", "P30D", "L1M", "P3M"].includes(p.value)
      ),
      years: RELATIVE_PRESETS.filter((p) => ["T1Y", "P1Y"].includes(p.value)),
    }),
    []
  );

  const handlePresetSelect = (preset: (typeof RELATIVE_PRESETS)[number]) => {
    updateClause({
      ...clause,
      value: {
        duration:
          clause.value?.duration === preset.value ? undefined : preset.value,
        startDate: undefined,
        endDate: undefined,
      },
    });
    popup.close();
  };

  const handleDateRangeChange = (val: any) => {
    if (val?.[0] && val?.[1]) {
      setTempDateRange({
        startDate: val[0].startOf("day").toISOString(),
        endDate: val[1].endOf("day").toISOString(),
      });
    } else {
      setTempDateRange({
        startDate: undefined,
        endDate: undefined,
      });
    }
  };

  const handleApply = () => {
    if (tempDateRange.startDate && tempDateRange.endDate) {
      updateClause({
        ...clause,
        value: {
          duration: undefined,
          startDate: tempDateRange.startDate,
          endDate: tempDateRange.endDate,
        },
      });
      setIsPickerOpen(false);
      popup.close();
    }
  };

  return (
    <div
      ref={ref}
      className={styles.datePickerWrapper}
      style={{ minWidth: 320 }}
    >
      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        {/* Recent Times */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: token.colorTextSecondary,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <ClockCircleOutlined />
            Recent
          </div>
          <Space wrap>
            {presetGroups.recent.map((preset) => (
              <Button
                key={preset.value}
                type={
                  preset.value === clause.value?.duration
                    ? "primary"
                    : "default"
                }
                size="small"
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </Space>
        </div>

        {/* Weeks */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: token.colorTextSecondary,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <CalendarOutlined />
            Weeks
          </div>
          <Space wrap>
            {presetGroups.weeks.map((preset) => (
              <Button
                key={preset.value}
                type={
                  preset.value === clause.value?.duration
                    ? "primary"
                    : "default"
                }
                size="small"
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </Space>
        </div>

        {/* Months */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: token.colorTextSecondary,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <CalendarOutlined />
            Months
          </div>
          <Space wrap>
            {presetGroups.months.map((preset) => (
              <Button
                key={preset.value}
                type={
                  preset.value === clause.value?.duration
                    ? "primary"
                    : "default"
                }
                size="small"
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </Space>
        </div>

        {/* Years */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: token.colorTextSecondary,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <HistoryOutlined />
            Years
          </div>
          <Space wrap>
            {presetGroups.years.map((preset) => (
              <Button
                key={preset.value}
                type={
                  preset.value === clause.value?.duration
                    ? "primary"
                    : "default"
                }
                size="small"
                onClick={() => handlePresetSelect(preset)}
              >
                {preset.label}
              </Button>
            ))}
          </Space>
        </div>

        {/* Custom Range */}
        <div>
          <div
            style={{
              fontSize: 12,
              color: token.colorTextSecondary,
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <CalendarOutlined />
            Custom Range
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              padding: "4px 8px",
              borderRadius: token.borderRadius,
              border: `1px solid ${
                !clause.value?.duration &&
                clause.value?.startDate &&
                clause.value?.endDate
                  ? token.colorPrimary
                  : "transparent"
              }`,
              background:
                !clause.value?.duration &&
                clause.value?.startDate &&
                clause.value?.endDate
                  ? token.colorPrimaryBg
                  : "transparent",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
              }}
            >
              <DatePicker
                style={{
                  flex: 1,
                  ...(!clause.value?.duration &&
                    clause.value?.startDate &&
                    clause.value?.endDate && {
                      backgroundColor: "transparent",
                      borderColor: token.colorPrimary,
                    }),
                }}
                value={
                  tempDateRange.startDate
                    ? dayjs(tempDateRange.startDate)
                    : null
                }
                onChange={(date) => {
                  setTempDateRange((prev) => ({
                    ...prev,
                    startDate: date?.startOf("day").toISOString(),
                  }));
                }}
                disabledDate={(date) => {
                  const endDate = tempDateRange.endDate
                    ? dayjs(tempDateRange.endDate)
                    : null;
                  return (
                    dayjs().isBefore(date, "day") ||
                    (!!endDate && date.isAfter(endDate))
                  );
                }}
                placeholder="Start date"
                inputReadOnly
              />
              <span>to</span>
              <DatePicker
                style={{
                  flex: 1,
                  ...(!clause.value?.duration &&
                    clause.value?.startDate &&
                    clause.value?.endDate && {
                      backgroundColor: "transparent",
                      borderColor: token.colorPrimary,
                    }),
                }}
                value={
                  tempDateRange.endDate ? dayjs(tempDateRange.endDate) : null
                }
                onChange={(date) => {
                  setTempDateRange((prev) => ({
                    ...prev,
                    endDate: date?.endOf("day").toISOString(),
                  }));
                }}
                disabledDate={(date) => {
                  const startDate = tempDateRange.startDate
                    ? dayjs(tempDateRange.startDate)
                    : null;
                  return (
                    dayjs().isBefore(date, "day") ||
                    (!!startDate && date.isBefore(startDate))
                  );
                }}
                placeholder="End date"
                inputReadOnly
              />
            </div>
          </div>
        </div>
      </Space>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          paddingTop: 12,
          marginTop: 16,
        }}
      >
        <Button
          size="small"
          onClick={() => {
            setIsPickerOpen(false);
            popup.close();
          }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          type="primary"
          onClick={handleApply}
          disabled={!tempDateRange.startDate || !tempDateRange.endDate}
        >
          Apply
        </Button>
      </div>
    </div>
  );
}
